import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock,
  ArrowRight,
  CheckCircle2,
  Users,
  Sparkles,
  Crown,
  EyeOff,
} from "lucide-react";
import IntroSuggestions from "@/components/IntroSuggestions/IntroSuggestions";
import NetworkThemes from "@/components/NetworkThemes/NetworkThemes";
import BreakthroughsPanel from "@/components/BreakthroughsPanel/BreakthroughsPanel";

import "./Network.css";

const TIERS = [
  {
    id: "member",
    label: "Member",
    price: "Free · by introduction",
    icon: Users,
    perks: [
      "Access to seasonal events in your circle",
      "Monthly dossier from the moderators",
      "Soft intros to people at your tables",
    ],
  },
  {
    id: "vip",
    label: "VIP",
    price: "$1,200 / yr",
    icon: Sparkles,
    highlight: true,
    perks: [
      "Everything in Member",
      "Reserved seat at VIP tables (curated to 6)",
      "Early access to all events, member-only salons",
      "Priority moderator matching",
    ],
  },
  {
    id: "founding",
    label: "Founding Circle",
    price: "By invitation",
    icon: Crown,
    perks: [
      "Everything in VIP",
      "Co-design the season's calendar with the steward",
      "Host your own table once per quarter",
      "Annual founding-circle retreat",
    ],
  },
];

const STATIC_AGG = {
  total_members: 1842,
  vip_count: 214,
  members_seeking: 1330,
  members_giving: 1207,
  top_interests: [
    { tag: "AI", count: 412 },
    { tag: "Founders", count: 367 },
    { tag: "Climate", count: 251 },
    { tag: "Design", count: 198 },
    { tag: "Policy", count: 143 },
    { tag: "Capital", count: 121 },
  ],
};

export default function Network() {
  const { user } = useAuth();
  const [agg, setAgg] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [selectedTier, setSelectedTier] = useState("vip");
  const [applyForm, setApplyForm] = useState({
    name: "",
    email: "",
    circle: "",
    referrer: "",
    note: "",
  });
  const [applySent, setApplySent] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      setAgg(STATIC_AGG);
      setApplyForm((f) => ({
        ...f,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  const submitMembership = async (e) => {
    e.preventDefault();
    setApplySent(true);
    toast.success("Application received. We'll be in touch.");
  };

  if (user === null) {
    return <div className="container-x networkLoading">Loading…</div>;
  }

  if (user === false) {
    return (
      <div className="networkPage" data-testid="network-page">
        <section className="container-x networkLockedSection">
          <Lock size={20} className="networkLockIcon" />
          <p className="overline networkOverline">The network</p>

          <h1 className="font-display networkLockedTitle">
            Members only.
            <br />
            <span className="networkTitleAccent">Sign in to see the room.</span>
          </h1>

          <p className="networkLockedBody">
            The network is the people, the seeking and the giving. Open to
            anyone who's registered — VIP members get reserved tables and
            curated intros.
          </p>

          <div className="networkCtaRow">
            <Link
              to="/register"
              className="btn-gold"
              data-testid="network-register-cta"
            >
              Create a profile <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="btn-ghost">
              Sign in
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="network-page" className="networkPage">
      <section
        className="networkHero"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x networkHeroInner">
          <p className="overline networkOverlineLg">The network</p>

          <div className="networkHeroGrid">
            <div className="networkHeroLeft">
              <h1 className="font-display networkHeroTitle">
                What the room{" "}
                <span className="networkTitleAccent">wants and offers.</span>
              </h1>
            </div>

            <div className="networkHeroRight">
              <p className="networkHeroBody">
                Privacy by design — you see the shape of the room (themes,
                counts, trends), not the people in it. Names are revealed inside
                an event lobby, where the people you're actually sitting with
                become visible.
              </p>

              <div className="networkPrivacyNote">
                <EyeOff size={12} className="networkPrivacyIcon" />
                <span>
                  Identities surface only after you're registered to an event.
                </span>
              </div>
            </div>
          </div>

          {agg && (
            <div className="networkAggGrid" data-testid="network-aggregates">
              {[
                ["Members", agg.total_members],
                ["VIP", agg.vip_count],
                ["Stating what they seek", agg.members_seeking],
                ["Stating what they give", agg.members_giving],
              ].map(([l, n]) => (
                <div key={l} className="glass networkAggCard">
                  <p className="font-display networkAggNum">{n}</p>
                  <p className="overline networkAggLabel">{l}</p>
                </div>
              ))}
            </div>
          )}

          {agg && agg.top_interests?.length > 0 && (
            <div
              className="networkTopInterests"
              data-testid="network-top-interests"
            >
              <p className="overline networkTopInterestsOverline">
                Top interests in the room
              </p>
              <div className="networkPills">
                {agg.top_interests.map((i) => (
                  <span key={i.tag} className="networkPill">
                    <span className="networkPillTag">{i.tag}</span>
                    <span className="networkPillCount">{i.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="section networkContentSection">
        <div className="container-x">
          <NetworkThemes />
          <IntroSuggestions />
        </div>
      </section>

      <BreakthroughsPanel scope="global" />

      <section
        id="apply-membership"
        className="section networkMembershipSection"
        data-testid="membership-section"
        style={{
          background: "linear-gradient(180deg, #0A1D1A 0%, #061412 100%)",
        }}
      >
        <div className="container-x">
          <div className="networkMembershipHeader">
            <div className="networkMembershipHeaderLeft">
              <p className="overline networkMembershipOverline">
                Membership tiers
              </p>
              <h2 className="font-display networkMembershipTitle">
                Get a seat at the
                <br />
                <span className="networkTitleAccent">
                  tables that don't get listed.
                </span>
              </h2>
            </div>
            <div className="networkMembershipHeaderRight">
              <p className="networkMembershipLead">
                vmeetio's exclusive membership is by introduction. Members get
                reserved seats at VIP tables, early access to the calendar, and
                personal invites to off-the-record salons.
              </p>
            </div>
          </div>

          <div className="networkTierGrid">
            {TIERS.map((t) => {
              const Icon = t.icon;
              const isSelected = selectedTier === t.id;

              return (
                <button
                  key={t.id}
                  onClick={() => {
                    setSelectedTier(t.id);
                    setShowApply(true);
                  }}
                  data-testid={`membership-tier-${t.id}`}
                  className={`networkTierCard ${isSelected ? "isSelected" : "isUnselected"}`}
                  type="button"
                >
                  {t.highlight && !isSelected && (
                    <span className="networkMostPopular">Most popular</span>
                  )}

                  <Icon size={22} className="networkTierIcon" />
                  <p className="overline networkTierLabel">{t.label}</p>
                  <p className="font-display networkTierPrice">{t.price}</p>

                  <ul className="networkPerks">
                    {t.perks.map((p) => (
                      <li key={p} className="networkPerkItem">
                        <CheckCircle2 size={14} className="networkPerkIcon" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          {showApply && (
            <div className="networkApplyGrid">
              <div className="networkApplyLeft">
                <p className="overline networkApplyOverline">Apply</p>
                <h3 className="font-display networkApplyTitle">
                  Tell us where you'd sit.
                </h3>
                <p className="networkApplyLead">
                  A short application is enough. We review on a rolling basis
                  and respond within two weeks. New members are introduced to
                  their circle by the steward.
                </p>
              </div>

              <div className="networkApplyRight">
                {applySent ? (
                  <div
                    className="glass networkApplySuccess"
                    data-testid="membership-success"
                  >
                    <CheckCircle2
                      className="networkApplySuccessIcon"
                      size={32}
                    />
                    <h3 className="font-display networkApplySuccessTitle">
                      Application received
                    </h3>
                    <p className="networkApplySuccessBody">
                      Welcome to the consideration round. We'll be in touch
                      within two weeks.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={submitMembership}
                    className="glass networkApplyForm"
                    data-testid="membership-form"
                  >
                    <div className="networkTwoCol">
                      <div>
                        <label className="overline networkLabel">
                          Full name
                        </label>
                        <input
                          required
                          value={applyForm.name}
                          onChange={(e) =>
                            setApplyForm({ ...applyForm, name: e.target.value })
                          }
                          data-testid="membership-name"
                          className="networkInput"
                        />
                      </div>

                      <div>
                        <label className="overline networkLabel">Email</label>
                        <input
                          required
                          type="email"
                          value={applyForm.email}
                          onChange={(e) =>
                            setApplyForm({
                              ...applyForm,
                              email: e.target.value,
                            })
                          }
                          data-testid="membership-email"
                          className="networkInput"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="overline networkLabel">
                        Which circle do you belong in?
                      </label>
                      <select
                        value={applyForm.circle}
                        onChange={(e) =>
                          setApplyForm({ ...applyForm, circle: e.target.value })
                        }
                        data-testid="membership-circle"
                        className="networkSelect"
                      >
                        <option value="">Pick one</option>
                        {[
                          "Founders",
                          "AI & Research",
                          "Climate",
                          "Design",
                          "Policy",
                          "Healthtech",
                        ].map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="overline networkLabel">
                        Who referred you? (optional)
                      </label>
                      <input
                        value={applyForm.referrer}
                        onChange={(e) =>
                          setApplyForm({
                            ...applyForm,
                            referrer: e.target.value,
                          })
                        }
                        data-testid="membership-referrer"
                        className="networkInput"
                      />
                    </div>

                    <div>
                      <label className="overline networkLabel">
                        Anything we should know?
                      </label>
                      <textarea
                        rows={3}
                        value={applyForm.note}
                        onChange={(e) =>
                          setApplyForm({ ...applyForm, note: e.target.value })
                        }
                        data-testid="membership-note"
                        className="networkTextarea"
                      />
                    </div>

                    <p className="networkApplyingFor">
                      Applying for:{" "}
                      <span className="networkApplyingTier">
                        {TIERS.find((t) => t.id === selectedTier)?.label}
                      </span>
                    </p>

                    <button
                      data-testid="membership-submit"
                      type="submit"
                      className="btn-gold networkSubmitBtn"
                    >
                      Submit application <ArrowRight size={16} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}

          {!showApply && (
            <div className="networkApplyCtaWrap">
              <button
                onClick={() => setShowApply(true)}
                data-testid="membership-show-apply"
                className="btn-gold networkInlineBtn"
                type="button"
              >
                Apply for membership <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
